import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Paperclip, X, File, Image as ImageIcon, Video, Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateFile, formatFileSize, getFileExtension } from '../utils/fileHelpers';
import { uploadMultipleFiles } from '../utils/firebaseUpload';
import MessageAttachment from '../components/MessageAttachment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 3;

const ExecutiveMessagingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // File attachment state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAssistantProfile();
  }, []);

  // Fetch threads only after assistant is loaded
  useEffect(() => {
    if (assistant) {
      fetchThreads();
      // Check for thread parameter in URL
      const threadParam = searchParams.get('thread');
      if (threadParam && threads.length > 0) {
        const thread = threads.find(t => t.executive_id === threadParam);
        if (thread) {
          setSelectedThread(thread);
        }
      }
    }
  }, [assistant]);

  useEffect(() => {
    if (selectedThread && assistant) {
      fetchMessages(selectedThread.executive_id);
      markMessagesAsRead(selectedThread.executive_id);
      
      // Poll for new messages every 10 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedThread.executive_id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedThread, assistant]);

  const fetchAssistantProfile = async () => {
    try {
      // First try to get from sessionStorage
      const storedAssistant = sessionStorage.getItem('assistant');
      if (storedAssistant) {
        const assistantData = JSON.parse(storedAssistant);
        setAssistant(assistantData);
        return;
      }
      
      // Fallback to API call
      const res = await fetch(`${BACKEND_URL}/api/assistant/profile`, {
        
      });
      if (res.ok) {
        const data = await res.json();
        setAssistant(data.assistant);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      navigate('/assistant-login');
    }
  };

  const fetchThreads = async () => {
    if (!assistant) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistants/${assistant.id}/threads`, {
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch threads:', err);
      setLoading(false);
    }
  };

  const fetchMessages = async (executiveId) => {
    if (!assistant || !executiveId) return;
    
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/assistants/${assistant.id}/messages?executive_id=${executiveId}`,
        {  }
      );
      
      if (res.ok) {
        const messages = await res.json();
        setMessages(messages || []);
        
        // Scroll to bottom
        setTimeout(() => {
          const messagesEnd = document.getElementById('messages-end');
          if (messagesEnd) {
            messagesEnd.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  };

  const markMessagesAsRead = async (executiveId) => {
    if (!assistant || !executiveId) return;
    
    try {
      await fetch(
        `${BACKEND_URL}/api/assistants/${assistant.id}/messages/mark-read`,
        {
          method: 'POST',
          
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ executive_id: executiveId })
        }
      );
      
      // Refresh threads to update unread count
      fetchThreads();
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedFiles.length + files.length > MAX_FILES) {
      setUploadError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }
    
    if (errors.length > 0) {
      setUploadError(errors.join('; '));
    } else {
      setUploadError(null);
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
    // Clear the input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedThread || !assistant) return;

    setSending(true);
    setUploadError(null);
    
    try {
      let attachments = [];
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        try {
          attachments = await uploadMultipleFiles(selectedFiles, (progress) => {
            setUploadProgress(progress);
          });
        } catch (uploadErr) {
          console.error('File upload failed:', uploadErr);
          setUploadError('Failed to upload files. Please try again.');
          setSending(false);
          return;
        }
      }
      
      // Send message with attachments
      const res = await fetch(
        `${BACKEND_URL}/api/assistants/${assistant.id}/messages`,
        {
          method: 'POST',
          
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'assistant',
            body: newMessage.trim(),
            executive_id: selectedThread.executive_id,
            executive_name: selectedThread.executive_name,
            attachments: attachments
          })
        }
      );

      if (res.ok) {
        setNewMessage('');
        setSelectedFiles([]);
        setUploadProgress(0);
        fetchMessages(selectedThread.executive_id);
        fetchThreads(); // Refresh thread list
      } else {
        setUploadError('Failed to send message');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setUploadError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Mobile: Show thread list OR chat view
  if (isMobile && selectedThread) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#F9FAFB' }}>
        {/* Mobile Header */}
        <div style={{
          padding: '16px',
          background: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => setSelectedThread(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={20} color="#3B82F6" />
          </button>
          <div>
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              {selectedThread.executive_name}
            </h3>
            <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
              {selectedThread.executive_email}
            </p>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.from === 'assistant' ? 'flex-end' : 'flex-start',
                marginBottom: '12px'
              }}
            >
              {/* Attachments */}
              {msg.attachments && msg.attachments.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: '8px',
                  maxWidth: '75%'
                }}>
                  {msg.attachments.map((attachment, idx) => (
                    <MessageAttachment key={idx} attachment={attachment} />
                  ))}
                </div>
              )}
              
              {/* Text message */}
              {msg.body && (
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '10px 14px',
                    borderRadius: '18px',
                    background: msg.from === 'assistant' ? '#3B82F6' : '#FFFFFF',
                    color: msg.from === 'assistant' ? '#FFFFFF' : '#0A0A0A',
                    boxShadow: msg.from !== 'assistant' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  <p style={{ fontSize: '15px', margin: 0, wordBreak: 'break-word' }}>
                    {msg.body}
                  </p>
                  <p style={{
                    fontSize: '11px',
                    margin: '4px 0 0 0',
                    opacity: 0.7
                  }}>
                    {formatTimestamp(msg.created_at)}
                  </p>
                </div>
              )}
            </div>
          ))}
          <div id="messages-end" />
        </div>

        {/* Input */}
        <div style={{
          padding: '12px',
          background: '#FFFFFF',
          borderTop: '1px solid #E5E7EB'
        }}>
          {/* File Previews */}
          {selectedFiles.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '8px',
              flexWrap: 'wrap'
            }}>
              {selectedFiles.map((file, idx) => (
                <div key={idx} style={{
                  padding: '6px 12px',
                  background: '#F3F4F6',
                  borderRadius: '16px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ color: '#374151' }}>{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      color: '#6B7280'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Upload Progress */}
          {sending && selectedFiles.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{
                height: '4px',
                background: '#E5E7EB',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  background: '#3B82F6',
                  width: `${uploadProgress}%`,
                  transition: 'width 0.3s'
                }} />
              </div>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}
          
          {/* Error Message */}
          {uploadError && (
            <div style={{
              padding: '8px',
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#991B1B',
              marginBottom: '8px'
            }}>
              {uploadError}
            </div>
          )}
          
          <form onSubmit={handleSendMessage} style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf,.docx,.mp4"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || selectedFiles.length >= MAX_FILES}
              style={{
                padding: '10px',
                background: 'transparent',
                border: '1px solid #D1D5DB',
                borderRadius: '50%',
                cursor: sending || selectedFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: sending || selectedFiles.length >= MAX_FILES ? 0.5 : 1
              }}
            >
              <Paperclip size={20} color="#6B7280" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              disabled={sending}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid #D1D5DB',
                borderRadius: '24px',
                fontSize: '15px',
                outline: 'none'
              }}
            />
            <button
              type="submit"
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: (newMessage.trim() || selectedFiles.length > 0) && !sending ? '#3B82F6' : '#D1D5DB',
                border: 'none',
                cursor: (newMessage.trim() || selectedFiles.length > 0) && !sending ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Send size={20} color="#FFFFFF" />
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Desktop or Mobile Thread List
  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      padding: isMobile ? '0' : '20px'
    }}>
      <div style={{
        maxWidth: isMobile ? '100%' : '1200px',
        margin: '0 auto',
        background: '#FFFFFF',
        borderRadius: isMobile ? '0' : '12px',
        boxShadow: isMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
        height: isMobile ? '100vh' : 'calc(100vh - 40px)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Thread List */}
        <div style={{
          width: isMobile ? '100%' : '360px',
          borderRight: isMobile ? 'none' : '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={() => navigate('/a-dashboard')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                color: '#3B82F6',
                borderRadius: '8px',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              title="Back to Dashboard"
            >
              <ArrowLeft size={20} />
            </button>
            <h2 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: 0 }}>
              Messages
            </h2>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                Loading...
              </div>
            ) : threads.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p style={{ color: '#6B7280', fontSize: '14px' }}>
                  No messages yet. Your assigned executive(s) can message you here.
                </p>
              </div>
            ) : (
              threads.map((thread) => (
                <div
                  key={thread.executive_id}
                  onClick={() => setSelectedThread(thread)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #F3F4F6',
                    cursor: 'pointer',
                    background: selectedThread?.executive_id === thread.executive_id ? '#F9FAFB' : '#FFFFFF',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedThread?.executive_id !== thread.executive_id) {
                      e.currentTarget.style.background = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedThread?.executive_id !== thread.executive_id) {
                      e.currentTarget.style.background = '#FFFFFF';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <h4 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: 0 }}>
                      {thread.executive_name}
                    </h4>
                    {thread.last_message_at && (
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        {formatTimestamp(thread.last_message_at)}
                      </span>
                    )}
                  </div>
                  <p style={{
                    color: '#6B7280',
                    fontSize: '14px',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {thread.last_message || 'No messages yet'}
                  </p>
                  {thread.unread_count > 0 && (
                    <div style={{
                      marginTop: '6px',
                      display: 'inline-block',
                      padding: '2px 8px',
                      background: '#3B82F6',
                      color: '#FFFFFF',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {thread.unread_count}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat View (Desktop) */}
        {!isMobile && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {!selectedThread ? (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9CA3AF'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>💬</p>
                  <p style={{ fontSize: '16px', margin: 0 }}>Select a conversation to start messaging</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid #E5E7EB'
                }}>
                  <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0' }}>
                    {selectedThread.executive_name}
                  </h3>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                    {selectedThread.executive_email}
                  </p>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.from === 'assistant' ? 'flex-end' : 'flex-start',
                        marginBottom: '16px'
                      }}
                    >
                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          marginBottom: '8px',
                          maxWidth: '60%'
                        }}>
                          {msg.attachments.map((attachment, idx) => (
                            <MessageAttachment key={idx} attachment={attachment} />
                          ))}
                        </div>
                      )}
                      
                      {/* Text message */}
                      {msg.body && (
                        <div
                          style={{
                            maxWidth: '60%',
                            padding: '12px 16px',
                            borderRadius: '18px',
                            background: msg.from === 'assistant' ? '#3B82F6' : '#F3F4F6',
                            color: msg.from === 'assistant' ? '#FFFFFF' : '#0A0A0A'
                          }}
                        >
                          <p style={{ fontSize: '15px', margin: 0, wordBreak: 'break-word', lineHeight: '1.5' }}>
                            {msg.body}
                          </p>
                          <p style={{
                            fontSize: '11px',
                            margin: '6px 0 0 0',
                            opacity: 0.7
                          }}>
                            {formatTimestamp(msg.created_at)}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  <div id="messages-end" />
                </div>

                {/* Input */}
                <div style={{
                  padding: '16px 20px',
                  borderTop: '1px solid #E5E7EB'
                }}>
                  {/* File Previews */}
                  {selectedFiles.length > 0 && (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '12px',
                      flexWrap: 'wrap'
                    }}>
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} style={{
                          padding: '8px 14px',
                          background: '#F3F4F6',
                          borderRadius: '18px',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Paperclip size={14} color="#6B7280" />
                          <span style={{ color: '#374151' }}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              display: 'flex',
                              color: '#6B7280'
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Progress */}
                  {sending && selectedFiles.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        height: '4px',
                        background: '#E5E7EB',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          background: '#3B82F6',
                          width: `${uploadProgress}%`,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <p style={{ fontSize: '13px', color: '#6B7280', margin: '6px 0 0 0' }}>
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  )}
                  
                  {/* Error Message */}
                  {uploadError && (
                    <div style={{
                      padding: '10px',
                      background: '#FEE2E2',
                      border: '1px solid #FCA5A5',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#991B1B',
                      marginBottom: '12px'
                    }}>
                      {uploadError}
                    </div>
                  )}
                  
                  <form onSubmit={handleSendMessage} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center'
                  }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending || selectedFiles.length >= MAX_FILES}
                      style={{
                        padding: '12px',
                        background: 'transparent',
                        border: '1px solid #D1D5DB',
                        borderRadius: '50%',
                        cursor: sending || selectedFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: sending || selectedFiles.length >= MAX_FILES ? 0.5 : 1
                      }}
                    >
                      <Paperclip size={20} color="#6B7280" />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      disabled={sending}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '24px',
                        fontSize: '15px',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
                      style={{
                        padding: '12px 24px',
                        background: (newMessage.trim() || selectedFiles.length > 0) && !sending ? '#3B82F6' : '#D1D5DB',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '24px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: (newMessage.trim() || selectedFiles.length > 0) && !sending ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {sending ? 'Sending...' : (
                        <>
                          <Send size={18} />
                          Send
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveMessagingPage;